/**
 * MusicPlayer 加载与缓存（抽离自 MusicPlayer.js）
 * 返回 loadMusic, checkCacheStatus, getStreamUrlForCache
 * 通过 refsRef / settersRef / callbacksRef 访问主文件 refs/state/callbacks，避免闭包陈旧。
 */
import { useCallback } from 'react';
import { getAbsoluteMediaUrl } from '../../utils/musicStreamUrls';
import { VIDEO_LOAD_TIMEOUT_MS, AUDIO_LOAD_TIMEOUT_MS } from './constants';

/**
 * 等待 audio 的 loadedmetadata 或超时/错误（i18n 通过 t 注入）。
 * @param {HTMLAudioElement} audio
 * @param {string} filePath - 实际请求的 URL（用于日志）
 * @param {number} timeout
 * @param {function} t - i18n 函数，如 t('player.audioLoadFailed', { file })
 * @param {string} [displayFileName] - 展示用文件名（如 music.file_path 的 basename 或歌曲标题），错误提示中显示此项而非 URL 片段
 */
function tryLoadAudioFile(audio, filePath, timeout, t, displayFileName) {
  const getMsg = (key, opts) => (typeof t === 'function' ? t(key, opts) : key);
  const getFileName = () => (displayFileName && String(displayFileName).trim()) || (filePath && String(filePath).split('/').pop()) || filePath || '';
  return new Promise((resolve, reject) => {
    // 先检查一次当前状态：避免“事件在监听器绑定前已触发”导致误判超时
    try {
      if (audio.readyState >= 1) { // HAVE_METADATA
        return resolve();
      }
    } catch (_) {}

    let done = false;
    const cleanup = () => {
      try { audio.removeEventListener('loadedmetadata', onOk); } catch (_) {}
      try { audio.removeEventListener('loadeddata', onOk); } catch (_) {}
      try { audio.removeEventListener('canplay', onOk); } catch (_) {}
      try { audio.removeEventListener('error', onError); } catch (_) {}
    };

    const onOk = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };
    const onError = (e) => {
      console.error('Audio load error:', e, 'File path:', filePath);
      if (done) return;
      done = true;
      cleanup();
      reject(new Error(getMsg('player.audioLoadFailed', { file: getFileName() })));
    };
    audio.addEventListener('loadedmetadata', onOk);
    audio.addEventListener('loadeddata', onOk);
    audio.addEventListener('canplay', onOk);
    audio.addEventListener('error', onError);
    setTimeout(() => {
      try {
        // 若依旧完全没有 metadata（readyState==0），视为超时；否则交给后续 play() 失败/卡死恢复处理
        if (!done && audio.readyState === 0) {
          done = true;
          cleanup();
          reject(new Error(getMsg('player.audioLoadTimeout', { file: getFileName() })));
        }
      } catch (_) {}
    }, timeout);
  });
}

async function tryCopyToClipboard(text) {
  try {
    const s = String(text || '');
    if (!s) return false;

    // 现代 API（通常要求 HTTPS / localhost）
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(s);
        return true;
      }
    } catch (_) {}

    // 兜底：execCommand（旧浏览器/非安全上下文）
    try {
      if (typeof document === 'undefined') return false;
      const el = document.createElement('textarea');
      el.value = s;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.top = '-9999px';
      el.style.left = '-9999px';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand && document.execCommand('copy');
      document.body.removeChild(el);
      return !!ok;
    } catch (_) {}
  } catch (_) {}
  return false;
}

/**
 * @param {{ refsRef: React.MutableRefObject<object>, settersRef: React.MutableRefObject<object>, callbacksRef: React.MutableRefObject<object> }}
 * refsRef.current: audioRef, videoRef, wasmDecodeAbortRef, wasmAudioUrlRef, lastUiTickRef, currentLyricIndexRef, isLoadingRef, durationRef, wasCachedRef, isProgressTrackingRef, playHistoryIntervalRef, activeMediaTypeRef, currentPlayingFormat, muted, volume, fadeInOut
 * settersRef.current: setLoading, setDuration, updatePlaying, setCurrentPlayingFormat, setVideoDimensions, setIsVideoFullscreen, setIsCached, setLyrics, setVisualFadeInToken, setIsVisualSwitching, clearLyricHighlight, updateMiniLyric
 * callbacksRef.current: getVideoFilePath, getCachedMusicMetadata, isFileCached, getMusicStreamUrl, getStreamUrl, rebuildAudioGraph, resumeAudioContext, loadLyrics, startProgressTracking, startPlayHistoryTracking, recordPlay, cacheMusicMetadata, cacheMusicFile, getCoverStreamUrl, getFadeStep, FADE_TICK_MS, isAdmin, t, showUiHint, showHintPlus, mpWarn, applyAudioFocusMode（tryLoadAudioFile 已内置于 useLoadMusic）
 */
export function useLoadMusic({ refsRef, settersRef, callbacksRef }) {
  const getStreamUrlForCache = useCallback((music, currentFormat, videoFilePath) => {
    const c = callbacksRef.current;
    if (!music) return null;
    return c?.getMusicStreamUrl?.(music, 'webm') ?? null;
  }, [callbacksRef]);

  const checkCacheStatus = useCallback(async (music) => {
    const refs = refsRef.current;
    const setters = settersRef.current;
    const callbacks = callbacksRef.current;
    if (!music || !music.id) {
      setters?.setIsCached?.(false);
      if (refs?.wasCachedRef) refs.wasCachedRef.current = false;
      return false;
    }
    try {
      const cachedMetadata = await callbacks?.getCachedMusicMetadata?.(music.id);
      if (!cachedMetadata) {
        setters?.setIsCached?.(false);
        return false;
      }
      const videoPath = callbacks?.getVideoFilePath?.(music);
      const streamUrlToCheck = getStreamUrlForCache(music, refs?.currentPlayingFormat, videoPath);
      if (streamUrlToCheck) {
        const isAudioCached = await callbacks?.isFileCached?.(streamUrlToCheck);
        const wasCached = refs?.wasCachedRef?.current;
        setters?.setIsCached?.(isAudioCached);
        if (refs?.wasCachedRef) refs.wasCachedRef.current = isAudioCached;
        if (isAudioCached && !wasCached) {
          try {
            window.dispatchEvent(new CustomEvent('tenryu:music-cached', { detail: { musicId: music.id } }));
          } catch (_) {}
        }
        return isAudioCached;
      }
      setters?.setIsCached?.(false);
      if (refs?.wasCachedRef) refs.wasCachedRef.current = false;
      return false;
    } catch (error) {
      console.warn('Failed to check cache status:', error);
      setters?.setIsCached?.(false);
      if (refs?.wasCachedRef) refs.wasCachedRef.current = false;
      return false;
    }
  }, [refsRef, settersRef, callbacksRef, getStreamUrlForCache]);

  const loadMusic = useCallback(async (music, shouldAutoPlay = false) => {
    const refs = refsRef.current;
    const setters = settersRef.current;
    const callbacks = callbacksRef.current;
    if (!music || !music.file_path) return;

    if (refs?.isLoadingRef) refs.isLoadingRef.current = true;
    setters?.setLoading?.(true);
    const audio = refs?.audioRef?.current;
    const video = refs?.videoRef?.current;
    const videoFilePath = callbacks?.getVideoFilePath?.(music);
    if (refs?.wasmDecodeAbortRef) refs.wasmDecodeAbortRef.current += 1;
    if (refs?.wasmAudioUrlRef?.current) {
      URL.revokeObjectURL(refs.wasmAudioUrlRef.current);
      refs.wasmAudioUrlRef.current = null;
    }

    if (refs?.lastUiTickRef) refs.lastUiTickRef.current = 0;
    if (refs?.currentLyricIndexRef) refs.currentLyricIndexRef.current = -1;

    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.load();
      } catch (e) {}
    }
    if (video && videoFilePath) {
      try {
        video.pause();
        video.currentTime = 0;
        video.load();
      } catch (e) {}
    }

    if (!shouldAutoPlay) setters?.updatePlaying?.(false);
    setters?.setDuration?.(0);
    setters?.clearLyricHighlight?.();
    setters?.updateMiniLyric?.(-1);
    setters?.setCurrentPlayingFormat?.(null);
    setters?.setVideoDimensions?.({ width: 0, height: 0 });
    setters?.setIsVideoFullscreen?.(false);

    try {
      if (!callbacks?.isAdmin) {
        try {
          const resp = await fetch(`/api/music/${music.id}/playback/check`, {
            method: 'GET',
            cache: 'no-store',
            credentials: 'include'
          });
          if (resp.status === 404) return;
          const data = await resp.json().catch(() => ({}));
          const allowed = !!(resp.ok && data?.data?.allowed !== false);
          if (!allowed) {
            const code = String(data?.code || data?.data?.code || '');
            if (code === 'PLAYBACK_REQUIRES_MEMBERSHIP') {
              callbacks?.showUiHint?.(callbacks?.t?.('player.playbackRequiresMembershipHint') || data?.message || callbacks?.t?.('auth.membershipFreeDesc'), { duration: 2600 });
            } else if (code === 'ACCESS_DENIED') {
              callbacks?.showUiHint?.(callbacks?.t?.('player.playbackAccessDeniedHint') || data?.message || callbacks?.t?.('player.unknownError'), { duration: 2200 });
            } else {
              const msg = data?.message || data?.error || `HTTP ${resp.status}`;
              callbacks?.showUiHint?.(msg, { duration: 2000 });
            }
            setters?.setLoading?.(false);
            if (refs?.isLoadingRef) refs.isLoadingRef.current = false;
            setters?.updatePlaying?.(false);
            return;
          }
        } catch (e) {}
      }

      if (video && videoFilePath) {
        const directVideoUrl = videoFilePath;
        if (audio) {
          try {
            audio.pause();
            audio.currentTime = 0;
            audio.removeAttribute('src');
            audio.load();
          } catch (_) {}
        }

        const tryLoadVideo = async (url) => {
          if (!url) return false;
          try {
            const muted = refs?.muted ?? false;
            const volume = refs?.volume ?? 1;
            const fadeInOut = refs?.fadeInOut ?? false;
            video.src = getAbsoluteMediaUrl(url);
            video.volume = muted ? 0 : volume;
            if (fadeInOut) video.volume = 0;
            video.playbackRate = 1.0;
            await new Promise((resolve, reject) => {
              const onLoaded = () => {
                video.removeEventListener('loadedmetadata', onLoaded);
                video.removeEventListener('error', onError);
                if (video.videoWidth && video.videoHeight) {
                  setters?.setVideoDimensions?.({ width: video.videoWidth, height: video.videoHeight });
                }
                resolve();
              };
              const onError = () => {
                video.removeEventListener('loadedmetadata', onLoaded);
                video.removeEventListener('error', onError);
                reject(new Error('video_load_failed'));
              };
              video.addEventListener('loadedmetadata', onLoaded);
              video.addEventListener('error', onError);
            });
            return true;
          } catch (_) {
            return false;
          }
        };

        const loadedVideo = await tryLoadVideo(directVideoUrl);

        if (!loadedVideo) {
          console.warn('[Video] Video not available, fallback to audio:', { musicId: music?.id, videoFilePath, directVideoUrl });
          try {
            video.pause();
            video.currentTime = 0;
            video.removeAttribute('src');
            video.load();
          } catch (_) {}
        } else {
          const muted = refs?.muted ?? false;
          const volume = refs?.volume ?? 1;
          const fadeInOut = refs?.fadeInOut ?? false;
          video.volume = muted ? 0 : volume;
          if (fadeInOut) video.volume = 0;
          video.playbackRate = 1.0;

          if (video && video.src) {
            await callbacks?.rebuildAudioGraph?.('video');
            setters?.setDuration?.(video.duration || 0);
            if (refs?.durationRef) refs.durationRef.current = video.duration || 0;

            let videoFileSize = null;
            try {
              const streamUrl = callbacks?.getStreamUrl?.(music, 'video');
              if (streamUrl) {
                const headResponse = await fetch(streamUrl, { method: 'HEAD', credentials: 'include' });
                const contentLength = headResponse.headers.get('Content-Length');
                if (contentLength) videoFileSize = parseInt(contentLength, 10);
              }
            } catch (_) {}

            const videoExt = (() => {
              try {
                const p = String(videoFilePath || '').split('?')[0];
                const ext = p.split('.').pop();
                return ext ? String(ext).toLowerCase() : 'video';
              } catch (_) {
                return 'video';
              }
            })();

            setters?.setCurrentPlayingFormat?.({
              format: videoExt,
              path: videoFilePath,
              bitrate: null,
              file_size: videoFileSize,
              sample_rate: null,
              channels: null,
              codec: `${videoExt.toUpperCase()} Video`
            });

            setters?.setLoading?.(false);
            if (refs?.isLoadingRef) refs.isLoadingRef.current = false;

            if (music.lyrics_type) {
              await callbacks?.loadLyrics?.(null, music.lyrics_type, music);
            } else {
              setters?.setLyrics?.([]);
            }

            if (music) {
              callbacks?.cacheMusicMetadata?.(music).then(() => {
                checkCacheStatus(music);
              }).catch(err => console.warn('Failed to cache music metadata:', err));

              const audioStreamUrlForVideo = getStreamUrlForCache(music, null, videoFilePath);
              if (audioStreamUrlForVideo) {
                callbacks?.cacheMusicFile?.(audioStreamUrlForVideo).then(() => {
                  setTimeout(() => {
                    checkCacheStatus(music);
                    try {
                      window.dispatchEvent(new CustomEvent('tenryu:music-cached', { detail: { musicId: music.id } }));
                    } catch (_) {}
                  }, 500);
                }).catch(err => console.warn('Failed to cache audio file for video:', err));
              }

              const coverStreamUrl = callbacks?.getCoverStreamUrl?.(music);
              if (coverStreamUrl) {
                callbacks?.cacheMusicFile?.(coverStreamUrl).catch(err => console.warn('Failed to cache cover image:', err));
              }
              setTimeout(() => checkCacheStatus(music), 3000);
            }

            if (shouldAutoPlay) {
              try {
                await callbacks?.resumeAudioContext?.();
                await video.play();
                setters?.updatePlaying?.(true);
                if (!refs?.isProgressTrackingRef?.current) callbacks?.startProgressTracking?.();
                if (refs?.playHistoryIntervalRef?.current === null) callbacks?.startPlayHistoryTracking?.();
                callbacks?.recordPlay?.();
                if (fadeInOut) {
                  setters?.setIsVisualSwitching?.(false);
                  setters?.setVisualFadeInToken?.((v) => v + 1);
                  const targetVolume = muted ? 0 : volume;
                  if (targetVolume > 0) {
                    let currentVol = 0;
                    const step = callbacks?.getFadeStep?.(0, targetVolume);
                    const FADE_TICK_MS = callbacks?.FADE_TICK_MS ?? 50;
                    const fadeInterval = setInterval(() => {
                      currentVol += step;
                      if (currentVol >= targetVolume) {
                        video.volume = targetVolume;
                        clearInterval(fadeInterval);
                      } else {
                        video.volume = currentVol;
                      }
                    }, FADE_TICK_MS);
                  }
                }
              } catch (e) {
                setters?.updatePlaying?.(false);
              }
            }
            return;
          }
        }
      }

      const streamUrl = callbacks?.getStreamUrl?.(music, 'webm');
      audio.src = getAbsoluteMediaUrl(streamUrl) || streamUrl;
      const muted = refs?.muted ?? false;
      const volume = refs?.volume ?? 1;
      const fadeInOut = refs?.fadeInOut ?? false;
      audio.volume = muted ? 0 : volume;
      audio.playbackRate = 1.0;
      if (fadeInOut) audio.volume = 0;

      // 提示里显示的“文件”：
      // - 调试/开发阶段：显示完整 URL（含路径与文件名），便于排查局域网/缓存/代理问题
      // - 生产阶段：显示期望的 webm 文件名（避免 URL 过长影响体验）
      const displayFileName = (() => {
        try {
          const isDev = process.env.NODE_ENV === 'development';
          if (isDev) {
            const abs = getAbsoluteMediaUrl(streamUrl) || streamUrl;
            return abs ? String(abs) : '';
          }
          const base = (music?.file_path && String(music.file_path).split('/').pop()) || '';
          if (base) return String(base).replace(/\.[^/.]+$/, '.webm');
        } catch (_) {}
        return music?.file_name || music?.title || '';
      })();
      await tryLoadAudioFile(audio, streamUrl, AUDIO_LOAD_TIMEOUT_MS, callbacks?.t, displayFileName);
      await callbacks?.rebuildAudioGraph?.('audio');

      let webmFileSize = null;
      try {
        const headResponse = await fetch(streamUrl, { method: 'HEAD' });
        const contentLength = headResponse.headers.get('Content-Length');
        if (contentLength) webmFileSize = parseInt(contentLength, 10);
      } catch (_) {}

      let actualBitrate = null;
      if (webmFileSize && audio.duration && audio.duration > 0) {
        actualBitrate = Math.round((webmFileSize * 8) / audio.duration);
      }

      const webmFormat = {
        format: 'webm',
        path: streamUrl,
        bitrate: actualBitrate,
        file_size: webmFileSize || null,
        sample_rate: music.sample_rate,
        channels: music.channels,
        codec: 'WebM'
      };
      setters?.setCurrentPlayingFormat?.(webmFormat);

      if (videoFilePath && video) {
        try {
          const videoStreamUrl = callbacks?.getStreamUrl?.(music, 'video');
          video.src = getAbsoluteMediaUrl(videoStreamUrl) || videoStreamUrl;
          video.volume = muted ? 0 : volume;
          video.playbackRate = 1.0;

          await new Promise((resolve, reject) => {
            const onLoadedMetadata = () => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              if (video.videoWidth && video.videoHeight) {
                setters?.setVideoDimensions?.({ width: video.videoWidth, height: video.videoHeight });
              }
              resolve();
            };
            const onError = (e) => {
              console.error('Video load error:', e, 'File path:', videoFilePath);
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              reject(new Error('Video load failed'));
            };
            video.addEventListener('loadedmetadata', onLoadedMetadata);
            video.addEventListener('error', onError);
            setTimeout(() => {
              if (video.readyState === 0) {
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                video.removeEventListener('error', onError);
                reject(new Error('Video load timeout'));
              }
            }, VIDEO_LOAD_TIMEOUT_MS);
          });

          if (video.duration && video.duration > 0) {
            setters?.setDuration?.(video.duration);
            if (audio) audio.currentTime = 0;
          }

          video.addEventListener('timeupdate', () => {
            if (audio && video.currentTime !== audio.currentTime) {
              audio.currentTime = video.currentTime;
            }
          });
        } catch (videoError) {
          console.warn('Failed to load video, falling back to audio:', videoError);
        }
      } else {
        setters?.setDuration?.(audio.duration);
      }

      if (webmFormat && audio.duration && audio.duration > 0) {
        const fileSize = webmFormat.file_size;
        if (fileSize) {
          const calculatedBitrate = Math.round((fileSize * 8) / audio.duration);
          setters?.setCurrentPlayingFormat?.(prev => ({ ...(prev || webmFormat), bitrate: calculatedBitrate }));
        }
      }

      if (music.lyrics_type) {
        await callbacks?.loadLyrics?.(null, music.lyrics_type, music);
      } else {
        setters?.setLyrics?.([]);
      }

      setters?.setLoading?.(false);
      if (refs?.isLoadingRef) refs.isLoadingRef.current = false;

      if (music) {
        callbacks?.cacheMusicMetadata?.(music).then(() => {
          checkCacheStatus(music);
        }).catch(err => console.warn('Failed to cache music metadata:', err));

        const videoPathToCache = callbacks?.getVideoFilePath?.(music);
        const mediaStreamUrlToCache = getStreamUrlForCache(music, refs?.currentPlayingFormat, videoPathToCache);
        if (mediaStreamUrlToCache) {
          callbacks?.cacheMusicFile?.(mediaStreamUrlToCache).then(() => {
            setTimeout(() => {
              checkCacheStatus(music);
              try {
                window.dispatchEvent(new CustomEvent('tenryu:music-cached', { detail: { musicId: music.id } }));
              } catch (_) {}
            }, 500);
          }).catch(err => console.warn('Failed to cache music file:', err));
        }

        const coverStreamUrl = callbacks?.getCoverStreamUrl?.(music);
        if (coverStreamUrl) {
          callbacks?.cacheMusicFile?.(coverStreamUrl).catch(err => console.warn('Failed to cache cover image:', err));
        }
        setTimeout(() => checkCacheStatus(music), 3000);
      } else {
        checkCacheStatus(music);
      }

      if (shouldAutoPlay && refs?.audioRef?.current) {
        try {
          await callbacks?.resumeAudioContext?.();
          await refs.audioRef.current.play();
          setters?.updatePlaying?.(true);
          callbacks?.startProgressTracking?.();
          callbacks?.startPlayHistoryTracking?.();
          callbacks?.recordPlay?.();

          if ('mediaSession' in navigator) {
            callbacks?.applyAudioFocusMode?.(true);
          }

          if (fadeInOut) {
            const hasVideo = videoFilePath && refs?.videoRef?.current;
            const mediaElement = hasVideo ? refs.videoRef.current : refs.audioRef.current;
            if (mediaElement && mediaElement.volume === 0) {
              setters?.setIsVisualSwitching?.(false);
              setters?.setVisualFadeInToken?.((v) => v + 1);
              const targetVolume = muted ? 0 : volume;
              if (targetVolume <= 0) return;
              let currentVol = 0;
              const step = callbacks?.getFadeStep?.(0, targetVolume);
              const FADE_TICK_MS = callbacks?.FADE_TICK_MS ?? 50;
              const fadeInterval = setInterval(() => {
                currentVol += step;
                if (currentVol >= targetVolume) {
                  mediaElement.volume = targetVolume;
                  clearInterval(fadeInterval);
                } else {
                  mediaElement.volume = currentVol;
                }
              }, FADE_TICK_MS);
            }
          }
        } catch (playError) {
          if (playError.name !== 'AbortError') {
            console.error('Auto play after load error:', playError);
          }
        }
      }
    } catch (error) {
      console.error('Load music error:', error);
      console.error('Music file path:', music?.file_path);
      if (refs?.isLoadingRef) refs.isLoadingRef.current = false;
      setters?.setLoading?.(false);
      setters?.updatePlaying?.(false);
      // 提示中显示的“文件”：
      // - 调试/开发阶段：完整 URL
      // - 生产阶段：文件名/标题（简洁）
      let fileName = '';
      try {
        const isDev = process.env.NODE_ENV === 'development';
        if (isDev) {
          // 优先展示浏览器实际用于加载的 URL（比 streamUrl 更接近真实）
          const el = refs?.audioRef?.current;
          const src = (el && (el.currentSrc || el.src)) ? String(el.currentSrc || el.src) : '';
          fileName = src || String(getAbsoluteMediaUrl(callbacks?.getStreamUrl?.(music, 'webm')) || callbacks?.getStreamUrl?.(music, 'webm') || '');
        } else {
          if (music?.file_path) {
            const base = String(music.file_path).split('/').pop();
            fileName = base ? String(base).replace(/\.[^/.]+$/, '.webm') : '';
          }
          if (!fileName && music?.file_name) fileName = String(music.file_name);
          if (!fileName && music?.title) fileName = String(music.title);
        }
      } catch (_) {}
      if (!fileName && error?.message) {
        const msg = String(error.message);
        if (msg.includes('/')) fileName = msg.split('/').pop();
        else if (msg.includes(':')) fileName = msg.split(':').pop().trim();
        else fileName = msg;
      }
      const hintMsg = callbacks?.t?.('player.audioLoadFailed', { file: fileName || callbacks?.t?.('hints.unknownFile') });
      // 调试阶段：自动复制到剪贴板，方便手机端直接粘贴/核对 URL
      try {
        if (process.env.NODE_ENV === 'development' && fileName) {
          const audioEl = refs?.audioRef?.current;
          const rs = (() => { try { return audioEl?.readyState; } catch (_) { return null; } })();
          const ns = (() => { try { return audioEl?.networkState; } catch (_) { return null; } })();
          const errCode = (() => { try { return audioEl?.error?.code; } catch (_) { return null; } })();
          const errMsg = (() => {
            try {
              const m = error?.message ? String(error.message) : '';
              return m;
            } catch (_) { return ''; }
          })();

          // 不阻塞 UI：异步做一次 HEAD，判断是否 404 / content-type 是否为 audio/webm
          (async () => {
            let headInfo = '';
            try {
              const absUrl = String(fileName || '');
              const resp = await fetch(absUrl, { method: 'HEAD', cache: 'no-store', credentials: 'include' });
              const ct = resp.headers.get('content-type') || '';
              const cl = resp.headers.get('content-length') || '';
              headInfo = `HEAD ${resp.status} ct=${ct} len=${cl}`;
            } catch (e) {
              headInfo = `HEAD failed: ${e?.message || String(e)}`;
            }

            const ua = (() => { try { return navigator?.userAgent || ''; } catch (_) { return ''; } })();
            const diag = [
              '[TENRYU] audio load failed (dev diag)',
              `url=${fileName}`,
              `readyState=${rs} networkState=${ns} mediaErrorCode=${errCode}`,
              `errorMessage=${errMsg}`,
              headInfo,
              `ua=${ua}`
            ].filter(Boolean).join('\n');

            try { console.warn(diag); } catch (_) {}
            tryCopyToClipboard(diag).catch(() => {});
          })();
        }
      } catch (_) {}
      callbacks?.showHintPlus?.(hintMsg);
    }
  }, [refsRef, settersRef, callbacksRef, checkCacheStatus, getStreamUrlForCache]);

  return { loadMusic, checkCacheStatus, getStreamUrlForCache };
}
